import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BrandMark, BrandWordmark } from "../components/BrandLogo";
import { SiteFooter } from "../components/SiteFooter";
import { API, FIT_CALL_BOOKING_LINK } from "../lib/config";
import { CheckCircle2, CalendarClock, Loader2 } from "lucide-react";

const SOURCE_OPTIONS = ["WhatsApp", "Website form", "Facebook / IG ads", "Google search", "Referrals", "Walk-ins", "Holiday camp campaigns"];

const initialForm = {
  centre_name: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  branches: "",
  programmes: "",
  monthly_enquiries: "",
  enquiry_sources: [],
  booking_process: "",
  biggest_leak: "",
};

const NEXT_STEPS = [
  "We review your onboarding answers before the call",
  "We walk through your current enquiry and trial process on the call",
  "You'll know within 48 hours if you're one of the 5 founding centres",
];

export default function ThankYouPage() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleSource = (src) => {
    setForm((f) => ({
      ...f,
      enquiry_sources: f.enquiry_sources.includes(src) ? f.enquiry_sources.filter((s) => s !== src) : [...f.enquiry_sources, src],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.centre_name.trim() || !form.contact_name.trim() || !form.contact_email.trim()) {
      toast.error("Please fill in your centre name, your name, and email.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contact_email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/onboarding`, form);
      setSubmitted(true);
      toast.success("Onboarding details received. See you on the fit call!");
    } catch (err) {
      toast.error("Something went wrong saving your details. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background noise-overlay flex flex-col">
      <header className="sticky top-0 z-50 bg-background border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-1.5 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" aria-label="EnquiryOps home" data-testid="thankyou-nav-logo">
            <BrandMark size={32} />
            <BrandWordmark className="text-lg" />
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 w-full flex-1">
        {/* Confirmation */}
        <div className="text-center mb-10 animate-fade-up">
          <span className="h-14 w-14 rounded-full bg-[var(--brand-weak)] flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-7 w-7 text-[var(--brand)]" aria-hidden="true" />
          </span>
          <h1 className="text-3xl sm:text-4xl font-semibold text-foreground" data-testid="thankyou-heading">Your Founding Centre slot is reserved</h1>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto leading-relaxed" data-testid="thankyou-terms-line">
            $199 received. Credited toward your setup fee if you proceed. Refunded in full if we're not a fit after the call.
          </p>
        </div>

        {/* Book fit call */}
        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)] mb-6" data-testid="thankyou-fit-call-card">
          <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex gap-4">
              <span className="h-9 w-9 rounded-full bg-[var(--navy-900)] text-[var(--paper)] font-semibold text-sm flex items-center justify-center shrink-0">1</span>
              <div>
                <p className="font-semibold text-foreground">Book your fit call</p>
                <p className="text-sm text-muted-foreground leading-relaxed mt-0.5">30 minutes, over Zoom or Google Meet.</p>
              </div>
            </div>
            <Button
              className="rounded-xl shrink-0 min-h-[44px] bg-[var(--brand)] text-[var(--paper)] hover:bg-[var(--brand-strong)] focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2"
              data-testid="thankyou-book-fit-call-button"
              onClick={() => window.open(FIT_CALL_BOOKING_LINK, "_blank", "noopener,noreferrer")}
            >
              <CalendarClock className="h-4 w-4 mr-1.5" /> Book Your 30-Minute Fit Call
            </Button>
          </CardContent>
        </Card>

        {/* What happens next */}
        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)] mb-6 bg-secondary" data-testid="thankyou-next-steps">
          <CardContent className="p-6">
            <p className="font-semibold text-sm text-foreground mb-3">What happens next</p>
            <ul className="space-y-2.5">
              {NEXT_STEPS.map((step) => (
                <li key={step} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 text-accent shrink-0 mt-0.5" aria-hidden="true" /> {step}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Onboarding form */}
        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
          <CardContent className="p-6 sm:p-8">
            <div className="flex gap-4 mb-6">
              <span className="h-9 w-9 rounded-full bg-[var(--navy-900)] text-[var(--paper)] font-semibold text-sm flex items-center justify-center shrink-0">2</span>
              <div>
                <p className="font-semibold text-foreground">Tell us about your centre</p>
                <p className="text-sm text-muted-foreground leading-relaxed mt-0.5">Takes about 3 minutes, and it makes the fit call twice as useful.</p>
              </div>
            </div>

            {submitted ? (
              <div className="bg-[var(--brand-weak)] border border-[var(--brand)] rounded-2xl p-8 text-center" data-testid="onboarding-success-state">
                <CheckCircle2 className="h-10 w-10 text-[var(--brand)] mx-auto mb-3" />
                <p className="font-heading font-semibold text-lg text-foreground">Details received. You're all set</p>
                <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto leading-relaxed">
                  We'll review your answers before the fit call. If you haven't booked it yet, do that above, and we'll see you there.
                </p>
                <Button variant="secondary" className="rounded-xl mt-5 border border-border" data-testid="onboarding-success-home-button" asChild>
                  <Link to="/">Back to home</Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="onboarding-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="centre_name">Centre name *</Label>
                    <Input id="centre_name" data-testid="onboarding-centre-name-input" className="h-11 rounded-xl" placeholder="e.g. Bright Minds Learning Lab" value={form.centre_name} onChange={(e) => set("centre_name", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="contact_name">Your name *</Label>
                    <Input id="contact_name" data-testid="onboarding-contact-name-input" className="h-11 rounded-xl" placeholder="e.g. Sarah Lim" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="contact_email">Email *</Label>
                    <Input id="contact_email" type="email" data-testid="onboarding-contact-email-input" className="h-11 rounded-xl" placeholder="you@centre.sg" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="contact_phone">WhatsApp number</Label>
                    <Input id="contact_phone" data-testid="onboarding-contact-phone-input" className="h-11 rounded-xl" placeholder="+65 9xxx xxxx" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Number of branches</Label>
                    <Select value={form.branches} onValueChange={(v) => set("branches", v)}>
                      <SelectTrigger className="h-11 rounded-xl" data-testid="onboarding-branches-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {["1 branch", "2 branches", "3 branches", "4+ branches"].map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Approx. monthly parent enquiries</Label>
                    <Select value={form.monthly_enquiries} onValueChange={(v) => set("monthly_enquiries", v)}>
                      <SelectTrigger className="h-11 rounded-xl" data-testid="onboarding-enquiries-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {["Under 20", "20-50", "50-100", "100-200", "200+"].map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="programmes">Programmes you run</Label>
                  <Input id="programmes" data-testid="onboarding-programmes-input" className="h-11 rounded-xl" placeholder="e.g. Primary Maths, Coding, Speech & Drama" value={form.programmes} onChange={(e) => set("programmes", e.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label>Where do enquiries come from today?</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {SOURCE_OPTIONS.map((src) => (
                      <label key={src} className="flex items-center gap-2.5 bg-secondary border border-border rounded-xl px-3.5 py-2.5 cursor-pointer hover:border-[var(--brand)] transition-colors">
                        <Checkbox
                          checked={form.enquiry_sources.includes(src)}
                          onCheckedChange={() => toggleSource(src)}
                          data-testid={`onboarding-source-${src.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        />
                        <span className="text-sm text-foreground">{src}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="booking_process">How do you handle bookings & follow-up today?</Label>
                  <Textarea id="booking_process" data-testid="onboarding-booking-process-input" className="rounded-xl min-h-[90px]" placeholder="e.g. Admin replies on WhatsApp, checks with teachers over group chat, tracks in a spreadsheet" value={form.booking_process} onChange={(e) => set("booking_process", e.target.value)} />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="biggest_leak">What's your biggest conversion leak right now?</Label>
                  <Textarea id="biggest_leak" data-testid="onboarding-biggest-leak-input" className="rounded-xl min-h-[90px]" placeholder="e.g. Parents go quiet after the trial and we don't follow up consistently" value={form.biggest_leak} onChange={(e) => set("biggest_leak", e.target.value)} />
                </div>

                <Button type="submit" size="lg" className="w-full rounded-xl h-12 bg-[var(--brand)] text-[var(--paper)] hover:bg-[var(--brand-strong)] focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" disabled={submitting} data-testid="onboarding-form-submit-button">
                  {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving</>) : "Submit onboarding details"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* What to prepare */}
        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)] mt-6 bg-secondary">
          <CardContent className="p-6">
            <p className="font-semibold text-sm text-foreground mb-3">Worth having handy for the fit call</p>
            <ul className="space-y-2">
              {["Rough enquiry volume by channel (even a guess helps)", "Your current trial booking steps, start to finish", "Last term's re-enrolment numbers, if you track them"].map((i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 text-accent shrink-0 mt-0.5" /> {i}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </main>

      <SiteFooter />
    </div>
  );
}
